"use client";
import React, {
    CSSProperties,
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from "react";
import {
    cropImg,
    file2dataUrl,
    getImgWidthHeight,
} from "@/src/utils/tryon/ImgUtil";
import { FmButton, FmDrawer, FmIcon, FmImage } from "@/ui-component";
import stl from "./cropper.module.scss";
import variable from "@/src/styles/variables.module.scss";
import classnames from "classnames";
import { useTranslation } from "@/src/i18n/client";

interface Props {
    file: File | null;
    onCancel: () => void;
    onSubmit: (blob: Blob, dataUrl: string) => void;
}

interface State {
    edgeL: number;
    edgeR: number;
    edgeT: number;
    edgeB: number;
    left: number;
    top: number;
    width: number;
    height: number;
    cropperBorderStyle: CSSProperties;
    idxOfDragging: number;
    change: number[];
    tooHigh: boolean;
    dataUrl: string;
    imgWidth: number;
    imgHeight: number;
    blob: Blob | null;
    rotate: number;
    boxWidth: number;
    boxHeight: number;
}

interface Action {
    type: string;
    payload?: any;
}

// 裁剪后图片的高/宽
const proportion = 1.146;

const ACTIONS = {
    SET_FILE_INFO: "SET_FILE_INFO",
    CANCEL: "CANCEL",
    SET_IDX_OF_DRAGGING: "SET_IDX_OF_DRAGGING",
    CHANGE: "CHANGE",
    ROTATE: "ROTATE",
    SET_USR_UPLOAD_FILE: "SET_USR_UPLOAD_FILE",
    SET_BOX_SIZE: "SET_BOX_SIZE",
};

const initialState: State = {
    edgeL: 0,
    edgeR: 0,
    edgeT: 0,
    edgeB: 0,
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    cropperBorderStyle: {
        left: "0",
        top: "0",
        width: "0",
        height: "0",
    },
    idxOfDragging: -1,
    change: [0, 0],
    tooHigh: true,
    dataUrl: "",
    imgWidth: 0,
    imgHeight: 0,
    blob: null,
    rotate: 0,
    boxWidth: 0,
    boxHeight: 0,
};

function useThrottle(func: any, wait: number) {
    const ref = useRef<boolean>(true);
    return function (...args: any) {
        if (ref.current) {
            ref.current = false;
            setTimeout(() => {
                ref.current = true;
            }, wait);
            func(...args);
        }
    };
}

function initCropperBorder(
    imgWidth: number,
    imgHeight: number,
    boxWidth: number,
    boxHeight: number,
) {
    if (!imgWidth || !boxWidth) {
        return {};
    } else {
        // 根据盒子的宽高和图片的原宽高，得到最终图片的宽高
        let tooHigh = imgHeight / imgWidth > boxHeight / boxWidth;
        if (tooHigh) {
            imgWidth = (imgWidth * boxHeight) / imgHeight;
            imgHeight = boxHeight;
        } else {
            imgHeight = (imgHeight * boxWidth) / imgWidth;
            imgWidth = boxWidth;
        }

        // 有了图片的宽高，那么可以算出裁剪框的初始样式。
        tooHigh = imgHeight / imgWidth > proportion;
        let width, height, left, top;
        if (tooHigh) {
            width = imgWidth - 1;
            height = width * proportion;
        } else {
            height = imgHeight - 1;
            width = height / proportion;
        }
        left = (boxWidth - width) / 2;
        top = (boxHeight - height) / 2;

        const edgeL = tooHigh ? (boxWidth - imgWidth) / 2 : 0;
        const edgeR = tooHigh ? (boxWidth - imgWidth) / 2 + imgWidth : boxWidth;
        const edgeT = tooHigh ? 0 : (boxHeight - imgHeight) / 2;
        const edgeB = tooHigh
            ? boxHeight
            : (boxHeight - imgHeight) / 2 + imgHeight;
        return {
            edgeL,
            edgeR,
            edgeT,
            edgeB,
            left,
            top,
            width,
            height,
            cropperBorderStyle: {
                left: left + "px",
                top: top + "px",
                width: width + "px",
                height: height + "px",
            },
        };
    }
}

function reducer(
    state: State = initialState,
    { type, payload }: Action,
): State {
    switch (type) {
        case ACTIONS.SET_FILE_INFO: {
            const info = initCropperBorder(
                payload.width,
                payload.height,
                state.boxWidth,
                state.boxHeight,
            );
            return {
                ...state,
                tooHigh: payload.tooHigh,
                dataUrl: payload.dataUrl,
                imgWidth: payload.width,
                imgHeight: payload.height,
                ...info,
            };
        }
        case ACTIONS.SET_BOX_SIZE: {
            const info = initCropperBorder(
                state.imgWidth,
                state.imgHeight,
                payload.boxWidth,
                payload.boxHeight,
            );
            return {
                ...state,
                boxWidth: payload.boxWidth,
                boxHeight: payload.boxHeight,
                ...info,
            };
        }
        case ACTIONS.CANCEL:
            return {
                ...initialState,
                boxWidth: state.boxWidth,
                boxHeight: state.boxHeight,
            };
        case ACTIONS.SET_IDX_OF_DRAGGING:
            return {
                ...state,
                idxOfDragging: payload,
            };
        case ACTIONS.CHANGE: {
            let {
                edgeL,
                edgeR,
                edgeT,
                edgeB,
                left,
                top,
                width,
                height,
                idxOfDragging,
            } = state;
            let notAllowChange = false;
            const minWidth = 50;
            const minHeight = minWidth * proportion;
            const [dx, dy] = payload;
            // top缩写t left缩写l bottom缩写b right缩写r center缩写c
            switch (idxOfDragging) {
                // 移动裁剪区域
                case -1:
                    left += dx;
                    top += dy;
                    if (left < edgeL) {
                        left = edgeL;
                    }
                    if (left + width > edgeR) {
                        left = edgeR - width;
                    }
                    if (top < edgeT) {
                        top = edgeT;
                    }
                    if (top + height > edgeB) {
                        top = edgeB - height;
                    }
                    break;
                // 拖拽左上角的点 右下角的点不动。
                case 0:
                    {
                        const rbX = left + width;
                        const rbY = top + height;
                        height -= dy;
                        width = height / proportion;
                        left = rbX - width;
                        top = rbY - height;
                    }
                    break;
                // 拖拽右上角的点 左下角的点不动
                case 1:
                    {
                        const lbY = top + height;
                        height -= dy;
                        width = height / proportion;
                        top = lbY - height;
                    }
                    break;
                // 拖拽左下角的点 右上角的点不动
                case 2:
                    {
                        const rtX = left + width;
                        height += dy;
                        width = height / proportion;
                        left = rtX - width;
                    }
                    break;
                // 拖拽右下角的点 左上上角的点不动
                case 3:
                    {
                        height += dy;
                        width = height / proportion;
                    }
                    break;
            }
            notAllowChange =
                left < edgeL ||
                top < edgeT ||
                left + width > edgeR ||
                top + height > edgeB ||
                width < minWidth ||
                height < minHeight;
            if (notAllowChange) {
                return state;
            } else {
                const cropperBorderStyle = {
                    left: left + "px",
                    top: top + "px",
                    width: width + "px",
                    height: height + "px",
                };
                return {
                    ...state,
                    left,
                    top,
                    width,
                    height,
                    cropperBorderStyle,
                };
            }
        }
        case ACTIONS.ROTATE: {
            let { imgWidth, imgHeight } = state;
            if (((state.rotate + 90) / 90) % 2 === 0) {
                [imgWidth, imgHeight] = [imgHeight, imgWidth];
            }
            return {
                ...state,
                rotate: (state.rotate + 90) % 360,
                ...initCropperBorder(
                    imgHeight,
                    imgWidth,
                    state.boxWidth,
                    state.boxHeight,
                ),
            };
        }
        default:
            console.warn("no action type", type, payload);
            return state;
    }
}

function Cropper({ file, onCancel, onSubmit }: Props) {
    const { t } = useTranslation("common");
    const imgBoxWidth = 604;
    const imgBoxHeight = 729;
    const svgConfigs = [
        {
            viewBox: "0 0 17 17",
            clsName: "lt",
            d: "M 0 0 L 0 16 L 2 16 L 2 2 L 16 2 L 16 0 Z",
        },
        {
            viewBox: "0 0 17 17",
            clsName: "rt",
            d: "M 0 0 L 0 2 L 14 2 L 14 16 L 16 16 L 16 0 Z",
        },
        {
            viewBox: "0 0 17 17",
            clsName: "lb",
            d: "M 0 0 L 0 16 L 16 16 L 16 14 L 2 14 L 2 0 Z",
        },
        {
            viewBox: "0 0 17 17",
            clsName: "rb",
            d: "M 16 16 L 16 0 L 14 0 L 14 14 L 0 14 L 0 16 Z",
        },
    ];

    const [
        {
            cropperBorderStyle,
            dataUrl,
            blob,
            rotate,
            imgWidth,
            imgHeight,
            left,
            top,
            width,
            height,
            boxWidth,
            boxHeight,
        },
        dispatch,
    ] = useReducer(reducer, initialState);

    // 这个ref存储两个值，用于拖拽时记录之前的位置
    const ref = useRef([Number.MIN_VALUE, Number.MIN_VALUE]);
    const cropperBox = useRef<HTMLDivElement | null>(null); // eslint-disable-line

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(initBoxSizeInfo, []);

    useEffect(() => {
        if (file) {
            file2dataUrl(file).then((dataUrl: any) => {
                getImgWidthHeight(dataUrl).then(({ width, height }: any) => {
                    const tooHigh = height / width > imgBoxHeight / imgBoxWidth;
                    dispatch({
                        type: ACTIONS.SET_FILE_INFO,
                        payload: { tooHigh, dataUrl, width, height },
                    });
                });
            });
        }
    }, [file]);

    function initBoxSizeInfo() {
        if (cropperBox.current !== null) {
            const { clientWidth, clientHeight } = cropperBox.current;
            if (clientWidth > 0) {
                const payload = {
                    boxWidth: clientWidth,
                    boxHeight: clientHeight,
                };
                dispatch({ type: ACTIONS.SET_BOX_SIZE, payload });
            } else {
                setTimeout(initBoxSizeInfo, 500);
            }
        } else {
            setTimeout(initBoxSizeInfo, 500);
        }
    }

    function onRotateImg() {
        dispatch({ type: ACTIONS.ROTATE });
    }

    function submit() {
        const box = cropperBox.current;
        // @ts-ignore
        const boxWidth = box.clientWidth;
        // @ts-ignore
        const boxHeight = box.clientHeight;
        cropImg({
            dataUrl,
            imgWidth,
            imgHeight,
            angle: rotate,
            boxWidth: boxWidth * 2,
            boxHeight: boxHeight * 2,
            left: left * 2,
            top: top * 2,
            width: width * 2,
            height: height * 2,
        }).then(({ blob, dataUrl }: any) => {
            dispatch({ type: ACTIONS.CANCEL });
            onSubmit(blob, dataUrl);
        });
    }

    function cancel() {
        dispatch({ type: ACTIONS.CANCEL });
        onCancel();
    }

    const onDragStart = useThrottle(function onDragStart(ev: any, idx: number) {
        ev.stopPropagation();
        function onDrag(ev: any) {
            ev.preventDefault();
            const screenX =
                ev.screenX !== undefined ? ev.screenX : ev.touches[0].screenX;
            const screenY =
                ev.screenY !== undefined ? ev.screenY : ev.touches[0].screenY;
            if (ref.current[0] !== Number.MIN_VALUE) {
                const change = [
                    screenX - ref.current[0],
                    screenY - ref.current[1],
                ];
                dispatch({ type: ACTIONS.CHANGE, payload: change });
            }
            ref.current = [screenX, screenY];
        }
        function onDragEnd() {
            window.removeEventListener("mousemove", onDrag);
            window.removeEventListener("touchmove", onDrag);
            window.removeEventListener("mouseup", onDragEnd);
            window.removeEventListener("touchend", onDragEnd);
            ref.current = [Number.MIN_VALUE, Number.MIN_VALUE];
        }
        window.addEventListener("mousemove", onDrag, { passive: false });
        window.addEventListener("touchmove", onDrag, { passive: false });
        window.addEventListener("mouseup", onDragEnd, { passive: false });
        window.addEventListener("touchend", onDragEnd, { passive: false });
        dispatch({ type: ACTIONS.SET_IDX_OF_DRAGGING, payload: idx });
    }, 16);

    const ratateImage = useMemo(() => {
        return {
            transform: `rotate(${rotate}deg)`,
            maxWidth: (rotate / 90) % 2 ? "7.29rem" : "6.04rem",
            maxHeight: (rotate / 90) % 2 ? "6.04rem" : "7.29rem",
        };
    }, [rotate]);

    return (
        <FmDrawer
            open={dataUrl !== ""}
            className={classnames(stl["cropper"], "cropper")}
            fullHeight
            closeable
            onClose={cancel}
            headerChildren={t("common:ImgTryOn.EditPicture")}
            headerClassName={classnames(stl["cropper-head"], "cropper-head")}
            closeablePosition={"right"}
            anchor={"bottom"}
            iconClassName={classnames(stl["cropper-cross"], "cropper-cross")}
        >
            <style jsx>{`
                .cropper-border > svg {
                    position: absolute;
                }
                svg.lt,
                svg.rt,
                svg.lb,
                svg.rb {
                    width: 34px;
                    padding: 8px 8px;
                }
                svg.lt {
                    left: -11px;
                    top: -11px;
                }
                svg.rt {
                    right: -0.24rem;
                    top: -11px;
                }
                svg.lb {
                    left: -11px;
                    bottom: -0.24rem;
                }
                svg.rb {
                    right: -0.24rem;
                    bottom: -0.24rem;
                }
                svg.lt,
                svg.rt,
                svg.lb,
                svg.rb {
                    width: 34px;
                    height: 34px;
                }

                .rotate > div {
                    font-size: 0.24rem;
                    line-height: 0.32rem;
                    font-family: var(--font-montserrat);
                    color: #333333;
                }
            `}</style>
            <div className={classnames(stl["cropper-area"])}>
                <div
                    ref={cropperBox}
                    className={classnames(stl["cropper-img-box"])}
                >
                    {dataUrl && (
                        <FmImage
                            style={ratateImage}
                            className={classnames(
                                stl["img-content"],
                                "img-content",
                            )}
                            image={dataUrl}
                            alt=""
                        />
                    )}
                    <svg
                        className={classnames(stl["mask"])}
                        viewBox={`0 0 ${boxWidth} ${boxHeight}`}
                    >
                        <path
                            d={`M 0 0 H ${boxWidth} V ${top} H 0 Z`}
                            fillOpacity="0.25"
                            fill="#000"
                        />
                        <path
                            d={`M 0 ${
                                top + height
                            } H ${boxWidth} V ${boxHeight} H 0 Z`}
                            fillOpacity="0.25"
                            fill="#000"
                        />
                        <path
                            d={`M 0 0 H ${left} V ${boxHeight} H 0 Z`}
                            fillOpacity="0.25"
                            fill="#000"
                        />
                        <path
                            d={`M ${
                                left + width
                            } 0 H ${boxWidth} V ${boxHeight} H ${
                                left + width
                            } Z`}
                            fillOpacity="0.25"
                            fill="#000"
                        />
                        <path
                            d={`M ${left} 0 H ${
                                left + width
                            } V ${top} H ${left} Z`}
                            fillOpacity="0.25"
                            fill="#000"
                        />
                        <path
                            d={`M ${left} ${top + height} H ${
                                left + width
                            } V ${boxHeight} H ${left} Z`}
                            fillOpacity="0.25"
                            fill="#000"
                        />
                        <path
                            d={`M 0 ${top} H ${left} V ${top + height} H 0 Z`}
                            fillOpacity="0.25"
                            fill="#000"
                        />
                        <path
                            d={`M ${left + width} ${top} H ${boxWidth} V ${
                                top + height
                            } H ${left + width} Z`}
                            fillOpacity="0.25"
                            fill="#000"
                        />
                    </svg>
                    {width > 0 && (
                        <div
                            onMouseDown={(ev: any) => onDragStart(ev, -1)}
                            onTouchStart={(ev: any) => onDragStart(ev, -1)}
                            style={cropperBorderStyle}
                            className={classnames(
                                stl["cropper-border"],
                                "cropper-border",
                            )}
                        >
                            {svgConfigs.map(({ viewBox, clsName, d }, idx) => (
                                <svg
                                    onMouseDown={(ev: any) =>
                                        onDragStart(ev, idx)
                                    }
                                    onTouchStart={(ev: any) =>
                                        onDragStart(ev, idx)
                                    }
                                    viewBox={viewBox}
                                    className={clsName}
                                    key={clsName}
                                >
                                    <path d={d} fill={variable.$primary} />
                                </svg>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            <div className={stl["button-container"]}>
                <div
                    onClick={onRotateImg}
                    className={classnames(stl["rotate"], "rotate")}
                >
                    <FmIcon style={{ fontSize: "24px" }} icon="icon--rotate" />
                    <span className={stl["rotate-text"]}>
                        {t("common:ImgTryOn.RotateThePicture")}
                    </span>
                </div>
                <FmButton
                    className={classnames(
                        stl["flex"],
                        stl["submit"],
                        "btn submit",
                    )}
                    onClick={submit}
                >
                    {t("common:ImgTryOn.Submit")}
                </FmButton>
                <FmButton
                    variant="outlined"
                    onClick={cancel}
                    className={classnames(
                        stl["cancel"],
                        stl["flex"],
                        "btn cancel",
                    )}
                >
                    {t("common:CANCEL")}
                </FmButton>
            </div>
        </FmDrawer>
    );
}

export default Cropper;
